import React, { useEffect, useState } from "react";
import { Flame, Snowflake, Wallet, Sparkles, Trophy } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";

const money = (n) => `$${Number(n).toLocaleString()}`;

export const XpEconomyCard = () => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/xp/status").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!d) return null;
  const { progress: p, streak: s, bankroll: b, pro_week: w, title: t } = d;
  const pickTitle = async (skill) => {
    try { await api.post("/titles/display", { skill: skill || null }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const start = async () => {
    setBusy(true);
    try { await api.post("/xp/pro-week/activate"); toast.success("Your Pro week has started."); load(); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="tc-card p-5 grid gap-4 sm:grid-cols-3" data-testid="xp-economy-card">
      <div>
        <div className="text-[11px] font-mono uppercase tracking-widest text-tc-xp flex items-center gap-1.5"><Trophy className="w-3.5 h-3.5" /> Level</div>
        <div className="font-heading text-xl font-bold mt-1" data-testid="xp-level">Lv {p.level} · {p.rank}</div>
        {t?.display_title && <div className="text-xs font-semibold text-tc-coach mt-0.5" data-testid="xp-display-title">{t.display_title}</div>}
        <div className="text-xs font-mono text-tc-muted mt-1" data-testid="xp-total">
          {p.max_level ? `${p.total_xp} XP · top level` : `${p.total_xp} / ${p.xp_for_next} XP`}
        </div>
      </div>
      <div>
        <div className="text-[11px] font-mono uppercase tracking-widest text-tc-pass flex items-center gap-1.5"><Flame className="w-3.5 h-3.5" /> Streak</div>
        <div className="font-heading text-xl font-bold mt-1" data-testid="xp-streak">{s.current}-day</div>
        <div className="text-xs font-mono text-tc-muted mt-1 flex items-center gap-1.5" data-testid="xp-freezes">
          <Snowflake className="w-3.5 h-3.5" /> {s.freezes}/{s.freeze_max} freezes{s.next_milestone ? ` · next milestone ${s.next_milestone} days` : ""}
        </div>
      </div>
      <div>
        <div className="text-[11px] font-mono uppercase tracking-widest text-tc-coach flex items-center gap-1.5"><Wallet className="w-3.5 h-3.5" /> Sim bankroll</div>
        <div className="font-heading text-xl font-bold mt-1" data-testid="xp-bankroll">{money(b.balance)}</div>
        <div className="text-xs text-tc-muted mt-1" data-testid="xp-bankroll-copy">
          {b.copy}{b.next_raise ? ` Raised to ${money(b.next_raise.amount)} at Level ${b.next_raise.level}.` : ""}
        </div>
      </div>
      {t && (
        <div className="sm:col-span-3 flex flex-wrap items-center gap-2 text-xs" data-testid="title-picker">
          <span className="font-mono uppercase tracking-widest text-tc-muted text-[11px]">Skill title</span>
          <select data-testid="title-select" value={t.display_skill || ""} onChange={(e) => pickTitle(e.target.value)}
            className="rounded-lg bg-tc-bg border border-white/10 px-2 py-1.5 text-sm outline-none focus:border-tc-coach">
            <option value="">None</option>
            {t.earned.map((x) => <option key={x.skill} value={x.skill}>{x.title}</option>)}
          </select>
          <span className="text-tc-muted">
            {t.earned.length ? `${t.earned.length} of ${t.available} earned. A title certifies a skill at ${t.threshold}%+ and unlocks nothing.`
              : `Reach ${t.threshold}% in any skill to earn its title.`}
          </span>
        </div>
      )}
      {(w.offered || w.active) && (
        <div className="sm:col-span-3 rounded-lg border border-tc-xp/30 bg-tc-xp/5 p-4 flex flex-wrap items-center justify-between gap-3" data-testid="pro-week-card">
          <div>
            <div className="font-semibold flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-tc-xp" />
              {w.active ? `Pro week active until ${new Date(w.expires_at).toLocaleString()}` : "You've earned a one-time Pro week"}</div>
            <p className="text-xs text-tc-sub mt-1">{w.analyses} AI analyses and {w.watches} watches for 7 days. {w.note}</p>
          </div>
          {w.offered && (
            <button data-testid="pro-week-start-btn" onClick={start} disabled={busy}
              className="rounded-lg bg-tc-xp text-tc-bg font-semibold px-4 py-2 text-sm hover:bg-tc-xp/90 transition-colors disabled:opacity-60">
              Start my Pro week
            </button>
          )}
        </div>
      )}
    </div>
  );
};
